/**
 * progressSnapshot — the ONE cross-platform child-progress snapshot schema
 * (WS4 product-data durability, 2026-08-19). Shared by web-child (progressSync.ts)
 * AND mobile (session.ts) so the two writers stop round-tripping through the same
 * `/children/{id}/progress` column with DIFFERENT shapes and clobbering each other.
 *
 * THE BUG THIS CLOSES: web-child used to PUT a computed STATS object
 * ({ currentLevel, totalLessons, averageScore, coins, … }) while mobile PUT a raw
 * localStorage KEY-MAP ({ learnwithai_skill_level, lwa_player_store_v2, … }) to the
 * SAME column, last-write-wins. web-child never restored at all (its api.getProgress
 * helper was dead code), so a fresh browser lost coins/rewards, per-lesson mastery
 * checkmarks, island progress, the spaced-review queue and score history — and a
 * mobile write zeroed the parent dashboard (which reads the stats fields).
 *
 * THE UNIFIED ENVELOPE (schemaVersion 2) keeps BOTH purposes without a collision:
 *
 *   {
 *     "schemaVersion": 2,
 *     "__snapshotUpdatedAt": 1699999999999,   // ms wall-clock version stamp
 *     "currentLevel": 4, "totalLessons": 12,   // ← DASHBOARD stats, TOP-LEVEL so the
 *     "averageScore": 85, "coins": 120, …,     //   account-api GET + admin parse them UNCHANGED
 *     "keys": {                                // ← RESTORE key-map (this module owns it)
 *       "lwa_player_store_v2": { … },          //   read back by web-child AND mobile on
 *       "learnwithai_curriculum_progress": {}  //   child-session start.
 *     }
 *   }
 *
 * • web-child writes stats (top-level) + keys. Mobile writes keys only (it has no
 *   stats compute); the server PRESERVES the last-known stats across a stats-less
 *   write so a mobile sync can't zero the parent dashboard — see the .NET
 *   ProgressSnapshotMerge.
 * • BOTH read `keys` back with a timestamp/absent-only guard so an equal/older
 *   snapshot never clobbers fresher local progress.
 *
 * MIGRATION: parseSnapshot() also reads the two LEGACY shapes already in prod —
 * a raw top-level key-map (legacy mobile, no envelope) and a bare stats object
 * (legacy web-child, nothing restorable) — without data loss.
 *
 * SECURITY: parseSnapshot only ever surfaces keys that pass isRestoreKey(), so a
 * rogue/foreign snapshot can never write arbitrary localStorage keys on restore.
 */

/** Current envelope schema version. Bump only on a breaking shape change. */
export const PROGRESS_SNAPSHOT_SCHEMA_VERSION = 2;

/** Snapshot-level version stamp field (ms). Rides inside the opaque blob the
 *  server stores verbatim so another device can tell whether a pulled snapshot is
 *  newer than the version it last synced — no per-key timestamps needed. */
export const SNAPSHOT_UPDATED_AT_FIELD = '__snapshotUpdatedAt';

/** localStorage key recording the snapshot VERSION this device last has in sync
 *  (the `__snapshotUpdatedAt` it last pushed or applied). Lets a pull tell whether
 *  the server snapshot is genuinely NEWER than what this device already reflects. */
export const PROGRESS_SYNCED_AT_KEY = 'learnwithai_progress_synced_at';

/**
 * The RESTORABLE localStorage keys — the durable, cross-device product state a
 * fresh device must get back. This is the UNION across both platforms (mobile
 * historically missed islands + the spaced-review queue; both are here now).
 *
 * DELIBERATELY EXCLUDED (see the excluded-list note at the bottom): identity /
 * session pointers, per-device sync bookkeeping, feature flags, the placement
 * grade (server-authoritative ChildProgramProfile), and book resume + the signed
 * gradebook (their OWN server columns/endpoints) — none of that belongs here.
 */
export const RESTORE_KEYS: readonly string[] = [
  // ── Coins / rewards ──────────────────────────────────────────────────────
  'lwa_player_store_v2',          // coins + owned store items (the reward economy)
  'lwa_arcade_passes_v1',         // earned arcade passes (never expire — real currency)
  'lwa_game_unlock_points_v1',    // progress toward the next game unlock
  'lwa_scene_v1',                 // buddy-pen scene / cosmetics the child arranged
  // ── Per-lesson mastery + score history ───────────────────────────────────
  'learnwithai_curriculum_progress', // per-lesson status map = the mastery checkmarks
  'learnwithai_curriculum_summary',  // resume/frontier summary snapshot
  'learnwithai_curriculum_variant',  // chosen curriculum edition (US/CA/UK)
  'learnwithai_lesson_scores',       // reading score history
  'learnwithai_writing_scores',      // writing score history
  'learnwithai_level_history',       // level-change history
  'learnwithai_skill_level',         // cached 1–6 skill level (seeded from the program SSOT)
  'learnwithai_child_prefs',         // voice / theme / music preferences (follow the child)
  // ── Islands ──────────────────────────────────────────────────────────────
  'lwa_quest_progress_v1',        // phonogram-island crystal progress
  // ── Buddy companion (P0-2, 2026-09-02) ────────────────────────────────────
  'lwa_buddy_v1',                 // buddy state: kind / name / care / equipped (buddyStore.ts, UNSCOPED)
  // ── Illustrated (text) book resume + read-with-me accuracy (P0-2) ─────────
  'learnwithai_book_progress',    // BookReaderPage resume position + read-with-me accuracy (feeds decoding mastery)
];

/**
 * Dynamic key PREFIXES — per-child keys suffixed with the child id (or a bare
 * legacy variant). Any localStorage key that startsWith one of these is restorable.
 */
export const RESTORE_KEY_PREFIXES: readonly string[] = [
  'lwa_child_setup_v1_',      // per-child setup (track + learning style)
  'lwa_assessment_completed', // placement-completion proof (scoped `_<id>` + legacy bare)
  'lwa_review_sched_v1',      // spaced-review queue: Record<concept, ConceptReviewState> (+ `_<id>`)
  'lwa_review_day_v1',        // spaced-review last-completed-day marker (+ `_<id>`)
  'lwa_review_reask_v1',      // spaced-review per-question re-ask counts (educator Wave 1.10, + `_<id>`)
  // ── Retention ledgers (educator Wave 1.10) — durable per-child learning state
  //    a fresh device must get back so long-term retention isn't lost. ──
  'lwa_gpc_ledger_v1',        // per-grapheme lesson→GPC ledger (gpcLedger.ts, + `_<id>`, bare fallback)
  'lwa_summative_',           // summative results + attempt counts: `lwa_summative_<variant>_<subject>_g<grade>`
                              //   and `lwa_summative_attempts_<variant>_<subject>_g<grade>` (summatives.ts)
  // ── Per-child durable gamification (P0-2, 2026-09-02) — all scoped `_<id>`
  //    with a bare legacy fallback (dailyReward/achievements/buddyJourney use
  //    scopedKey(PREFIX) = `${PREFIX}_${id}`, PREFIX when no active child). ──
  'lwa_streak_v1',            // day-streak ledger { lastDay, count } — the daily-habit hook
                              //   ⚠ MERGED BY MAX/latest-day in applyRestoreKeys (never last-writer),
                              //   so a stale device can't reset a live streak. See streakMerge() below.
  'lwa_achievements_v1',      // earned badges/trophies (AchievementRecord[])
  'lwa_buddy_journey_v1',     // egg-hatch buddy journey state { hatched, buddyKind, favoriteColor, hatchedAt }
                              //   (does NOT match the `lwa_buddy_journey_enabled_v1` flag — 'enabled' ≠ 'v1')
];

/** True when `key` is part of the durable, cross-device restore set. */
export function isRestoreKey(key: string): boolean {
  if (RESTORE_KEYS.includes(key)) return true;
  for (const prefix of RESTORE_KEY_PREFIXES) {
    if (key.startsWith(prefix)) return true;
  }
  return false;
}

/** Minimal Storage surface both platforms satisfy (browser localStorage). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  readonly length: number;
  key(index: number): string | null;
}

/** Enumerate every present key in a StorageLike (handles the localStorage Proxy). */
function allKeys(storage: StorageLike): string[] {
  const out: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k != null) out.push(k);
  }
  return out;
}

/**
 * Gather the current child's restorable localStorage into a key→value map,
 * parsing JSON values (falling back to the raw string). Exact inverse of
 * applyRestoreKeys — a round-trip preserves values byte-for-byte.
 */
export function collectRestoreKeys(storage: StorageLike): Record<string, unknown> {
  const map: Record<string, unknown> = {};
  for (const k of allKeys(storage)) {
    if (!isRestoreKey(k)) continue;
    const raw = storage.getItem(k);
    if (raw == null) continue;
    try { map[k] = JSON.parse(raw); }
    catch { map[k] = raw; }
  }
  return map;
}

/** Dashboard stats carried at the TOP LEVEL of the envelope (web-child only). */
export type SnapshotStats = Record<string, unknown>;

export interface AssembleSnapshotInput {
  /** The restore key-map (collectRestoreKeys). */
  keys: Record<string, unknown>;
  /** Optional dashboard stats spread at the top level (web-child); omit on mobile. */
  stats?: SnapshotStats;
  /** Version stamp; defaults to Date.now(). */
  now?: number;
}

export interface AssembledSnapshot {
  /** The JSON string to PUT as `{ snapshot }`. */
  json: string;
  /** The `__snapshotUpdatedAt` stamp used — record it as PROGRESS_SYNCED_AT_KEY. */
  updatedAt: number;
}

/**
 * Build the unified envelope. Stats (when present) are SPREAD at the top level so
 * the account-api GET + admin aggregators keep parsing `currentLevel`/`totalLessons`/
 * `coins`/… unchanged; the restore key-map lives under `keys`.
 */
export function assembleSnapshot(input: AssembleSnapshotInput): AssembledSnapshot {
  const updatedAt = typeof input.now === 'number' ? input.now : Date.now();
  const envelope: Record<string, unknown> = {
    ...(input.stats ?? {}),
    schemaVersion: PROGRESS_SNAPSHOT_SCHEMA_VERSION,
    [SNAPSHOT_UPDATED_AT_FIELD]: updatedAt,
    keys: input.keys,
  };
  return { json: JSON.stringify(envelope), updatedAt };
}

export interface ParsedSnapshot {
  /** 2 for the envelope; 1 for either legacy shape. */
  schemaVersion: number;
  /** `__snapshotUpdatedAt` (0 when the snapshot is legacy/unstamped). */
  updatedAt: number;
  /** Restorable key→value map, ALREADY filtered to isRestoreKey() keys. */
  keys: Record<string, unknown>;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Keep only entries whose key is in the restore set (defense in depth). */
function filterRestore(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (isRestoreKey(k)) out[k] = v;
  }
  return out;
}

/**
 * Parse a raw snapshot string into { schemaVersion, updatedAt, keys }, tolerating:
 *  • the v2 envelope (has a `keys` object) — keys = envelope.keys;
 *  • a legacy raw key-map (legacy mobile — restore keys at the top level);
 *  • a legacy stats object (legacy web-child — no restore keys → keys = {}).
 * Returns null only for `{}`/empty/corrupt input.
 */
export function parseSnapshot(raw: string | null | undefined): ParsedSnapshot | null {
  if (!raw || raw === '{}') return null;
  let obj: unknown;
  try { obj = JSON.parse(raw); }
  catch { return null; }
  if (!isPlainObject(obj)) return null;

  const updatedAt = typeof obj[SNAPSHOT_UPDATED_AT_FIELD] === 'number'
    ? (obj[SNAPSHOT_UPDATED_AT_FIELD] as number) : 0;

  // v2 envelope: keys live under `keys`.
  if (isPlainObject(obj.keys)) {
    return {
      schemaVersion: typeof obj.schemaVersion === 'number' ? obj.schemaVersion : PROGRESS_SNAPSHOT_SCHEMA_VERSION,
      updatedAt,
      keys: filterRestore(obj.keys),
    };
  }

  // Legacy: restore keys (if any) sit at the top level (the meta/stats fields are
  // dropped by filterRestore, so a legacy stats object yields an empty key-map).
  return { schemaVersion: 1, updatedAt, keys: filterRestore(obj) };
}

export interface ApplyRestoreResult {
  /** Keys actually written to storage. */
  appliedKeys: string[];
  /** True when the pulled snapshot was newer than this device's last sync. */
  serverIsNewer: boolean;
}

/** The scoped/bare day-streak key (dailyReward.ts STREAK_KEY + scopedKey). */
const STREAK_KEY_PREFIX = 'lwa_streak_v1';

/** True for the day-streak key (bare `lwa_streak_v1` or scoped `lwa_streak_v1_<id>`). */
function isStreakKey(key: string): boolean {
  return key === STREAK_KEY_PREFIX || key.startsWith(`${STREAK_KEY_PREFIX}_`);
}

interface StreakLedger { lastDay: string; count: number }

/** Shape-guard for the day-streak ledger { lastDay: "YYYY-MM-DD", count: number }. */
function asStreakLedger(v: unknown): StreakLedger | null {
  if (isPlainObject(v) && typeof v.lastDay === 'string' && typeof v.count === 'number') {
    return { lastDay: v.lastDay, count: v.count };
  }
  return null;
}

/**
 * MAX/latest-day merge for the day-streak (P0-2): NEVER last-writer. Returns the
 * ledger to keep so a stale device can never RESET a live streak on restore.
 *  • keep the HIGHER count (the child's real streak length wins);
 *  • on an equal count, keep the LATER lastDay (fresher activity day);
 *  • if only one side parses, keep that one; if neither parses, keep local.
 * `local`/`server` are the RAW stored/pulled string values (or null).
 * Returns null when local should be kept unchanged (no write needed).
 */
export function streakMerge(local: string | null, server: unknown): string | null {
  const serverLedger = asStreakLedger(
    typeof server === 'string' ? safeParse(server) : server,
  );
  if (serverLedger == null) return null; // nothing usable server-side → keep local
  const localLedger = local != null ? asStreakLedger(safeParse(local)) : null;
  if (localLedger == null) return JSON.stringify(serverLedger); // no local → take server
  // Higher count wins; tie broken by the later (lexicographically greater ISO) day.
  const serverWins =
    serverLedger.count > localLedger.count ||
    (serverLedger.count === localLedger.count && serverLedger.lastDay > localLedger.lastDay);
  return serverWins ? JSON.stringify(serverLedger) : null;
}

function safeParse(raw: string): unknown {
  try { return JSON.parse(raw); } catch { return raw; }
}

/**
 * Merge a parsed snapshot's key-map into storage with the version guard:
 *  • when the snapshot is NEWER than this device last synced → overwrite (true
 *    cross-device catch-up);
 *  • otherwise fill ONLY keys absent locally, so an equal/older snapshot never
 *    clobbers fresher local progress;
 *  • a legacy/unstamped snapshot (updatedAt 0) stays absent-only.
 * On a newer snapshot the device's PROGRESS_SYNCED_AT_KEY advances to its stamp.
 */
export function applyRestoreKeys(
  storage: StorageLike,
  parsed: ParsedSnapshot,
  opts: { syncedAtKey?: string } = {},
): ApplyRestoreResult {
  const syncedAtKey = opts.syncedAtKey ?? PROGRESS_SYNCED_AT_KEY;
  const localTs = Number(storage.getItem(syncedAtKey)) || 0;
  const serverIsNewer = parsed.updatedAt > localTs;
  const appliedKeys: string[] = [];
  for (const [k, v] of Object.entries(parsed.keys)) {
    if (!isRestoreKey(k)) continue; // already filtered, but never trust the blob
    // P0-2: the day-streak NEVER uses the whole-blob last-writer/absent-only rule —
    // a stale device (older snapshot) must not reset a live streak, and a fresher
    // local streak must survive a server-newer pull. Merge by MAX count / latest day.
    if (isStreakKey(k)) {
      const merged = streakMerge(storage.getItem(k), v);
      if (merged != null) { storage.setItem(k, merged); appliedKeys.push(k); }
      continue;
    }
    if (serverIsNewer || storage.getItem(k) == null) {
      storage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v));
      appliedKeys.push(k);
    }
  }
  if (serverIsNewer) storage.setItem(syncedAtKey, String(parsed.updatedAt));
  return { appliedKeys, serverIsNewer };
}

/*
 * ── DELIBERATELY DEVICE-LOCAL / OWNED ELSEWHERE (never in the restore set) ───
 *  • lwa_placement_grade_v1_<id>  — server-authoritative (ChildProgramProfile +
 *    assessed grade), seeded by ui-shared/programOnboarding/sessionSeed; syncing it
 *    here could fight the level SSOT (the "Starter" class of bug).
 *  • auth_token / child_auth_token / learnwithai_active_child — identity/session,
 *    per-device, must NEVER travel in a progress blob.
 *  • learnwithai_progress_synced_at, lwa_session_count(ed) — per-device sync/session
 *    bookkeeping.
 *  • feature flags (lwa_reward_loop_v1, lwa_learning_engine_v1, lwa_light_theme_v1,
 *    lwa_buddy_journey_enabled_v1, …) — device/dev toggles, not child progress.
 *  • graphic-novel (comic) book resume — its OWN /children/{id}/reading-progress
 *    column. NOTE: the ILLUSTRATED (text) book resume `learnwithai_book_progress`
 *    IS restored here (P0-2) — it is plain localStorage with no server column of
 *    its own, and its read-with-me accuracy feeds decoding mastery.
 *  • the signed per-lesson gradebook — its OWN ChildLessonResults rows.
 *
 * ── IN THE RESTORE SET SINCE 2026-09-02 (P0-2, were being LOST on device change) ──
 *  • lwa_streak_v1(_<id>)     — day-streak; MAX/latest-day merge, not last-writer.
 *  • lwa_achievements_v1(_<id>) — earned badges/trophies.
 *  • lwa_buddy_journey_v1(_<id>) + lwa_buddy_v1 — buddy journey + companion state.
 *  • learnwithai_book_progress — illustrated-book resume + read-with-me accuracy.
 *
 * ── ADDED educator Wave 1.10 (retention state that a fresh device must recover) ──
 *  • lwa_gpc_ledger_v1(_<id>)  — per-grapheme lesson→GPC ledger (drives GPC-level
 *    spaced review); losing it re-teaches mastered correspondences from scratch.
 *  • lwa_summative_*            — summative results + attempt counts per
 *    variant/subject/grade; the record of "show what you know" checkpoints.
 *  • lwa_review_reask_v1(_<id>) — per-question re-ask counts (the ≤2 identical
 *    re-ask cap); travels with the spaced-review queue it belongs to.
 */
